import React, { useCallback, useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  startOfDay,
  isBefore,
} from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ScheduleOccurrence } from '../../hooks/useSchedule';
import { LessonDatePopover } from './LessonDatePopover';
import { SCHEDULE_PAST_OPACITY_CLASSES, getLessonCalendarChipStyleProps } from '@/utils/lessonSlotColors';
import { useAgeGroupLookups } from '@/queries/ageGroups';
import {
  COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES,
  extraEventStackKey,
  formatConsumerCalendarEquipmentSummary,
  type ConsumerCalendarExtraEvent,
} from '@/utils/consumerCalendarEvents';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface TermLessonCalendarProps {
  occurrences: ScheduleOccurrence[];
  termStart?: Date;
  termEnd?: Date;
  academyId?: string;
  onReportAbsence?: (occurrence: ScheduleOccurrence) => void;
  onRequestRefund?: (occurrence: ScheduleOccurrence) => void;
  lessonsPath?: string;
  /** Academy owner: remove student from session or term */
  onRemoveFromLesson?: (occurrence: ScheduleOccurrence) => void;
  /** Dots (guardian schedule) vs labeled chips (academy-style month grid) */
  variant?: 'dots' | 'chips';
  extraEvents?: ConsumerCalendarExtraEvent[];
  onExtraEventClick?: (event: ConsumerCalendarExtraEvent) => void;
}

export function TermLessonCalendar({
  occurrences,
  termStart,
  termEnd,
  academyId,
  onReportAbsence,
  onRequestRefund,
  lessonsPath,
  onRemoveFromLesson,
  variant = 'dots',
  extraEvents = [],
  onExtraEventClick,
}: TermLessonCalendarProps) {
  const resolvedAcademyId = academyId ?? occurrences[0]?.academyId;
  const { ageGroupById } = useAgeGroupLookups(resolvedAcademyId);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    if (termStart && termEnd) {
      if (now < termStart) return termStart;
      if (now > termEnd) return termEnd;
    }
    return now;
  });
  const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null);

  const handleRemoveFromLesson = useCallback(
    (occurrence: ScheduleOccurrence) => {
      setOpenPopoverKey(null);
      onRemoveFromLesson?.(occurrence);
    },
    [onRemoveFromLesson]
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const canPrev =
    !termStart || monthStart > termStart;
  const canNext =
    !termEnd || monthEnd < termEnd;

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, ScheduleOccurrence[]>();
    for (const occ of occurrences) {
      const key = format(occ.date, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(occ);
    }
    return map;
  }, [occurrences]);

  const extraEventsByDate = useMemo(() => {
    const map = new Map<string, ConsumerCalendarExtraEvent[]>();
    for (const event of extraEvents) {
      const key = format(event.date, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return map;
  }, [extraEvents]);

  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [calendarStart, calendarEnd]);

  const isOccurrencePast = (occDate: Date) => {
    const now = new Date();
    return isBefore(startOfDay(occDate), startOfDay(now));
  };

  const getOccurrenceChipProps = (occ: ScheduleOccurrence) =>
    getLessonCalendarChipStyleProps(
      {
        ballColor: occ.ballColor,
        ageGroupId: occ.ageGroupId,
        lessonType: (occ.lessonType as 'group' | 'private' | 'camp') ?? 'group',
      },
      {
        ageGroupById,
        isPast: isOccurrencePast(occ.date),
        overrideType:
          occ.status === 'cancelled' ? 'cancelled' : occ.status === 'postponed' ? 'postponed' : null,
        isStandalonePrivate: occ.bookingScope === 'package',
        isCamp: occ.lessonType === 'camp',
      }
    );

  const useChips = variant === 'chips';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <h3 className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
            disabled={!canPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
            disabled={!canNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 sm:gap-1.5">
            {DAYS.map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {calendarDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayOccurrences = occurrencesByDate.get(key) ?? [];
              const dayExtraEvents = extraEventsByDate.get(key) ?? [];
              const totalDayItems = dayOccurrences.length + dayExtraEvents.length;
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTermDay =
                (!termStart || day >= termStart) &&
                (!termEnd || day <= termEnd);

              return (
                <div
                  key={key}
                  className={`rounded-md border p-1 ${
                    useChips ? 'min-h-[5.25rem] sm:min-h-36 md:min-h-40 sm:p-1.5' : 'min-h-[4rem]'
                  } ${
                    isCurrentMonth ? 'bg-background' : 'bg-muted/30'
                  } ${isTermDay ? 'border-primary/20' : 'border-border'}`}
                >
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {format(day, 'd')}
                  </div>
                  <div className={useChips ? 'flex flex-col gap-0.5' : 'flex flex-wrap gap-0.5'}>
                    {dayOccurrences.slice(0, 3).map((occ) => {
                      const popoverKey = `${occ.bookingId}-${occ.date.getTime()}`;
                      const chipProps = getOccurrenceChipProps(occ);
                      return (
                      <Popover
                        key={popoverKey}
                        open={openPopoverKey === popoverKey}
                        onOpenChange={(open) => setOpenPopoverKey(open ? popoverKey : null)}
                      >
                        <PopoverTrigger asChild>
                          {useChips ? (
                            <button
                              type="button"
                              className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate hover:ring-2 hover:ring-offset-1 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary ${chipProps.className}`}
                              style={chipProps.style}
                              aria-label={`${occ.lessonName} on ${format(occ.date, 'MMM d')}`}
                            >
                              <span className="truncate">{occ.lessonName}</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={`w-2 h-2 rounded-full hover:ring-2 hover:ring-offset-1 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary ${
                                isOccurrencePast(occ.date) ? SCHEDULE_PAST_OPACITY_CLASSES : ''
                              } ${chipProps.className}`}
                              style={{
                                backgroundColor:
                                  chipProps.style?.backgroundColor ?? chipProps.style?.borderColor,
                              }}
                              aria-label={`${occ.lessonName} on ${format(occ.date, 'MMM d')}`}
                            />
                          )}
                        </PopoverTrigger>
                        <PopoverContent className="w-80" align="start">
                          <LessonDatePopover
                            occurrence={occ}
                            onReportAbsence={onReportAbsence}
                            onRequestRefund={onRequestRefund}
                            lessonsPath={lessonsPath}
                            onRemoveFromLesson={
                              onRemoveFromLesson ? handleRemoveFromLesson : undefined
                            }
                          />
                        </PopoverContent>
                      </Popover>
                      );
                    })}
                    {dayExtraEvents.slice(0, Math.max(0, 3 - dayOccurrences.length)).map((event) => {
                      const equipmentSummary = formatConsumerCalendarEquipmentSummary(
                        event.equipmentHires
                      );
                      const chipLabel = equipmentSummary
                        ? `${event.title} · ${equipmentSummary}`
                        : event.title;
                      return (
                      <button
                        key={extraEventStackKey(event)}
                        type="button"
                        onClick={() => onExtraEventClick?.(event)}
                        className={
                          useChips
                            ? `w-full text-left text-[10px] px-1 py-0.5 rounded truncate hover:ring-2 hover:ring-offset-1 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary ${COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES}`
                            : `w-2 h-2 rounded-full hover:ring-2 hover:ring-offset-1 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary ${COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES}`
                        }
                        aria-label={`${event.title} court hire on ${format(event.date, 'MMM d')}${equipmentSummary ? ` with ${equipmentSummary}` : ''}`}
                      >
                        {useChips ? <span className="truncate">{chipLabel}</span> : null}
                      </button>
                      );
                    })}
                    {totalDayItems > 3 &&
                      (useChips ? (
                        <div className="text-[9px] text-gray-500">
                          +{totalDayItems - 3}
                        </div>
                      ) : (
                        <Tooltip
                          content={[
                            ...dayOccurrences.slice(3).map((o) => o.lessonName),
                            ...dayExtraEvents.slice(Math.max(0, 3 - dayOccurrences.length)).map((e) => e.title),
                          ].join(', ')}
                        >
                          <span className="text-[10px] text-gray-500 cursor-help">
                            +{totalDayItems - 3}
                          </span>
                        </Tooltip>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
            {useChips ? (
              <>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-blue-500" />
                  Group
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-purple-500" />
                  Private (in term)
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-orange-500" />
                  Private (package)
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-teal-600" />
                  Camp
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-blue-500 opacity-50" />
                  Past
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-red-500" />
                  Cancelled
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-4 rounded-sm bg-rose-500" />
                  Postponed
                </span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                  Scheduled
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500 opacity-50" />
                  Past
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500/80" />
                  Cancelled
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-rose-500/80" />
                  Postponed
                </span>
              </>
            )}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
